'use client'

import { useMemo, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useSearchParams } from 'next/navigation'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  BriefcaseIcon,
  CalendarIcon,
  EyeIcon,
  DownloadIcon,
  MoreHorizontalIcon,
  Trash2Icon,
  SquareMenuIcon,
  UserIcon,
  MailIcon,
  BookmarkCheckIcon,
  RefreshCwIcon
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { DeleteConfirmationDialog, LoadingState, SortableHeader } from '@/components/partials'
import { JobApplicationsFilters } from '@/components/partials/job-applications'
import {
  useJobApplications,
  useDeleteJobApplication,
  useBulkDeleteJobApplications,
  useDownloadJobApplicationPdf
} from '@/hooks'
import { useJobApplicationsStore } from '@/store'
import { JobApplicationsFilterParams } from '@/types/job-application'
import { SortEnum } from '@/enums'
import { formatDateTime } from '@/lib/utils'
import { hasPermission } from '@/lib/permissions'
import { BulkDeleteResponse } from '@/types/general'
import { useMeStore } from '@/store'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const { meState } = useMeStore()
  const currentUser = meState.response
  const canDelete = hasPermission(currentUser, 'JOB_APPLICATIONS', 'DELETE')
  const canShow = hasPermission(currentUser, 'JOB_APPLICATIONS', 'SHOW')

  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)

  const selectedIds = useJobApplicationsStore(state => state.selectedJobApplicationIds)
  const toggleSelection = useJobApplicationsStore(state => state.toggleJobApplicationSelection)
  const selectAll = useJobApplicationsStore(state => state.selectAllJobApplications)
  const deselectAll = useJobApplicationsStore(state => state.deselectAllJobApplications)
  const isSelected = useJobApplicationsStore(state => state.isJobApplicationSelected)

  const filters = useMemo<JobApplicationsFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())
    const readParam = params.get('read')

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.DESC,
      sortBy: (params.get('sortBy') as JobApplicationsFilterParams['sortBy']) || 'createdAt',
      q: params.get('q') || undefined,
      read: readParam !== null ? readParam === 'true' : undefined,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined
    }
  }, [searchParams])

  const { data, isLoading, error, refetch, isFetching } = useJobApplications(filters)
  const { mutate: deleteMutate, isPending: isDeleting } = useDeleteJobApplication()
  const { mutate: bulkDeleteMutate, isPending: isBulkDeleting } = useBulkDeleteJobApplications()
  const { mutate: downloadPdf, isPending: isDownloading } = useDownloadJobApplicationPdf()

  const totalPages = data?.pages ?? 1
  const currentPage = data?.page ?? filters.page ?? 1
  const pageSize = data?.size ?? filters.size ?? 20

  const updateUrlParams = (newFilters: Partial<JobApplicationsFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value.toString())
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<JobApplicationsFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/user-interactions/job-applications')
  }

  const handleSort = (sortBy: string, sort: SortEnum) => {
    handleFiltersChange({ sortBy: sortBy as JobApplicationsFilterParams['sortBy'], sort, page: 1 })
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleView = (id: string) => {
    router.push(`/user-interactions/job-applications/${id}`)
  }

  const handleDelete = (id: string) => {
    setDeleteId(id)
  }

  const handleBulkDelete = () => {
    if (selectedIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const handleSelectAll = () => {
    if (data && data.items.length > 0) {
      if (selectedIds.length === data.items.length) {
        deselectAll()
      } else {
        selectAll(data.items)
      }
    }
  }

  const confirmDelete = () => {
    if (deleteId) {
      deleteMutate(deleteId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['job-applications'] })
          toast.success(t('deleted_successfully'))
          setDeleteId(null)
        },
        onError: (error: any) => {
          toast.error(error.response?.data?.message ?? t('error_deleting'))
          setDeleteId(null)
        }
      })
    }
  }

  const confirmBulkDelete = () => {
    if (selectedIds.length > 0) {
      bulkDeleteMutate(selectedIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['job-applications'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response?.status === 422) {
            toast.error(error.response.data?.items?.ids || t('error_deleting'))
          } else {
            toast.error(t('error_deleting'))
          }
          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const renderActionItems = (id: string, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleView(id)
          }}>
          <EyeIcon className="h-4 w-4" />
          {t('job_application_details')}
        </ItemComponent>
        {canShow && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              downloadPdf(id)
            }}
            disabled={isDownloading}>
            <DownloadIcon className="h-4 w-4" />
            {t('download_pdf')}
          </ItemComponent>
        )}
        {canDelete && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleDelete(id)
            }}
            className="text-destructive focus:text-destructive">
            <Trash2Icon className="h-4 w-4" />
            {t('delete')}
          </ItemComponent>
        )}
      </>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BriefcaseIcon className="h-5 w-5" />
                {t('job_applications')}
              </CardTitle>
              <CardDescription>{t('job_applications_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {canDelete && selectedIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete} disabled={isBulkDeleting}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedIds.length})</span>
                  </div>
                </Button>
              )}
              <JobApplicationsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {data && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            <Checkbox
                              checked={data.items.length > 0 && selectedIds.length === data.items.length}
                              onCheckedChange={handleSelectAll}
                              onClick={e => e.stopPropagation()}
                              className="cursor-pointer"
                            />
                          </div>
                        </TableHead>
                        <TableHead className="min-w-40">
                          <SortableHeader
                            sortBy="firstname"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <UserIcon className="h-4 w-4" />
                            {t('applicant')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-40">
                          <SortableHeader
                            sortBy="email"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <MailIcon className="h-4 w-4" />
                            {t('email')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-32">
                          <SortableHeader
                            sortBy="phone"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            {t('phone')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-24 text-center">
                          <SortableHeader
                            sortBy="readAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <BookmarkCheckIcon className="h-4 w-4" />
                            {t('read_status')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-40 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.items.map((item, index) => (
                          <ContextMenu key={item.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`cursor-pointer transition-colors ${
                                  !item.readAt
                                    ? 'bg-blue-50 font-semibold hover:bg-blue-100 dark:bg-blue-950/30 dark:hover:bg-blue-900/40'
                                    : `hover:bg-muted/50 ${index % 2 === 0 ? 'bg-background' : 'bg-muted/25'}`
                                }`}
                                onClick={() => handleView(item.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    <Checkbox
                                      checked={isSelected(item.id)}
                                      onCheckedChange={() => toggleSelection(item.id)}
                                      onClick={e => e.stopPropagation()}
                                      className="cursor-pointer"
                                    />
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <p className="font-medium">{`${item.firstname} ${item.lastname}`}</p>
                                  {item.position && <p className="text-muted-foreground text-xs">{item.position}</p>}
                                </TableCell>
                                <TableCell>{item.email}</TableCell>
                                <TableCell>{item.phone}</TableCell>
                                <TableCell className="text-center">
                                  {item.readAt ? (
                                    <Badge variant="success">{t('read')}</Badge>
                                  ) : (
                                    <Badge variant="secondary">{t('unread')}</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-center">{formatDateTime(item.createdAt, locale)}</TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={e => e.stopPropagation()}
                                        className="h-8 w-8 p-0">
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(item.id, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(item.id, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={data.total}
                  pageSize={pageSize}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={!!deleteId}
        onCancel={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
      />

      <DeleteConfirmationDialog
        open={bulkDeleteConfirmOpen}
        onCancel={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={confirmBulkDelete}
        isDeleting={isBulkDeleting}
      />
    </>
  )
}

export default List
